import { useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { TimeControl } from "@chessla/shared";
import { fetchGames, type GameListItem } from "@/features/games/games.api";

export interface ChatSelectionContext {
  format?: TimeControl;
  gameId?: string;
  label: string;
}
const FORMATS: readonly TimeControl[] = ["rapid", "blitz", "bullet", "daily"];
const dateLabel = (value: string): string =>
  new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));

export const ChatContextPicker = ({
  value,
  disabled,
  onChange,
}: {
  value: ChatSelectionContext | null;
  disabled?: boolean;
  onChange: (value: ChatSelectionContext | null) => void;
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [games, setGames] = useState<readonly GameListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<TimeControl | null>(value?.format ?? null);
  const [query, setQuery] = useState("");
  const requestRevision = useRef(0);
  const visibleGames = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return games.filter(
      (game) =>
        (!filter || game.timeControl === filter) &&
        (!needle || `${game.opponent} ${game.openingName ?? ""}`.toLocaleLowerCase().includes(needle)),
    );
  }, [filter, games, query]);
  const close = (): void => setOpen(false);
  const openPicker = (): void => {
    setOpen(true);
    if (games.length || loading) return;
    const revision = ++requestRevision.current;
    setLoading(true);
    setFailed(false);
    void fetchGames(20)
      .then((result) => {
        if (revision === requestRevision.current) setGames(result);
      })
      .catch(() => {
        if (revision === requestRevision.current) setFailed(true);
      })
      .finally(() => {
        if (revision === requestRevision.current) setLoading(false);
      });
  };
  const chooseAccount = (): void => {
    onChange(null);
    setFilter(null);
    close();
  };
  return (
    <>
      <Button
        aria-label="Choose coaching context"
        disabled={disabled}
        onClick={openPicker}
        startIcon={<TuneIcon />}
        sx={{
          minHeight: 42,
          maxWidth: { xs: 210, sm: 360 },
          px: 1.3,
          borderRadius: 0,
          border: "1px solid",
          borderColor: "rgba(240,236,226,.42)",
          color: "text.primary",
          fontSize: ".62rem",
          fontWeight: 900,
          letterSpacing: ".045em",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value?.label ?? "ACCOUNT-WIDE"}
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullScreen={mobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          square: true,
          sx: {
            bgcolor: "background.default",
            backgroundImage: "none",
            border: { sm: "2px solid" },
            borderColor: "primary.main",
            boxShadow: { sm: "10px 10px 0 rgba(0,0,0,.45)" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: 2,
            p: { xs: 2, sm: 2.5 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.65rem", sm: "2.25rem" },
                lineHeight: 0.95,
                fontWeight: 900,
                letterSpacing: "-.04em",
              }}
            >
              CHOOSE THE EVIDENCE.
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary", fontSize: ".76rem" }}>
              Account-wide is default. Narrow to one format or game only when it helps.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close coaching context"
            onClick={close}
            sx={{ width: 44, height: 44, borderRadius: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography
            sx={{ mb: 0.8, fontSize: ".58rem", fontWeight: 900, letterSpacing: ".12em", color: "secondary.main" }}
          >
            SCOPE
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5,1fr)" }, gap: 0.75 }}>
            <Button
              aria-pressed={!value}
              variant={!value ? "contained" : "outlined"}
              onClick={chooseAccount}
              sx={{ borderRadius: 0, minHeight: 48, fontSize: ".62rem" }}
            >
              ACCOUNT
            </Button>
            {FORMATS.map((format) => (
              <Button
                key={format}
                aria-pressed={value?.format === format && !value.gameId}
                variant={value?.format === format && !value.gameId ? "contained" : "outlined"}
                onClick={() => {
                  onChange({ format, label: `${format.toUpperCase()} GAMES` });
                  setFilter(format);
                  close();
                }}
                sx={{ borderRadius: 0, minHeight: 48, fontSize: ".62rem" }}
              >
                {format}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "end", gap: 1, mt: 3 }}>
            <TextField
              fullWidth
              label="Search opponent or opening"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              inputProps={{ "aria-label": "Search games by opponent or opening" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
            {(query || filter) && (
              <Button
                onClick={() => {
                  setQuery("");
                  setFilter(null);
                }}
                sx={{ minHeight: 56, borderRadius: 0 }}
              >
                CLEAR
              </Button>
            )}
          </Box>
          <Typography
            sx={{
              mt: 2.5,
              mb: 1,
              fontSize: ".58rem",
              fontWeight: 900,
              letterSpacing: ".12em",
              color: "text.secondary",
            }}
          >
            RECENT GAMES · UP TO 20
          </Typography>
          {loading && (
            <Typography role="status" sx={{ py: 2, color: "text.secondary", fontSize: ".75rem" }}>
              Loading your recent games…
            </Typography>
          )}
          {failed && (
            <Typography role="alert" sx={{ py: 2, color: "error.main", fontSize: ".75rem" }}>
              Games could not be loaded. Account and format context still work.
            </Typography>
          )}
          {!loading && !failed && visibleGames.length === 0 && (
            <Typography sx={{ py: 2, color: "text.secondary", fontSize: ".75rem" }}>
              No matching recent games.
            </Typography>
          )}
          <Box sx={{ display: "grid", gap: 0.75 }}>
            {visibleGames.map((game) => (
              <Button
                key={game.id}
                aria-label={`Select game against ${game.opponent}`}
                onClick={() => {
                  const format = game.timeControl as TimeControl;
                  onChange({
                    gameId: game.id,
                    format,
                    label: `${format.toUpperCase()} · ${game.opponent.toUpperCase()}`,
                  });
                  setFilter(format);
                  close();
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 2,
                  minHeight: 64,
                  p: 1.25,
                  borderRadius: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.primary",
                  textAlign: "left",
                  justifyContent: "stretch",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: ".78rem", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {game.opponent}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      color: "text.secondary",
                      fontSize: ".62rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {game.openingName ?? "Opening unavailable"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: ".64rem", fontWeight: 900 }}>{game.result}</Typography>
                  <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: ".58rem" }}>
                    {dateLabel(game.playedAt)}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
